import { useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Folder, Plus, Search, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '#/components/ui/sheet'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '#/components/ui/dialog'
import { Input } from '#/components/ui/input'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { Switch } from '#/components/ui/switch'
import { Button } from '#/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { ApiError, usePlans, type PlanListItem } from '#/lib/api'
import { usePublishTemplate } from '#/lib/api-flywheel'
import { PlanLink } from '#/components/common/plan-link'
import { m } from '#/paraglide/messages'
import { fmtDate } from '#/lib/format'

type Sort = 'newest' | 'oldest' | 'title'

/**
 * Publish a finished plan as a public template (Phase 5). The server refuses with
 * 402 `pro_only` and 409 `not_enough_evidence` (< 5 verified tasks) — both are shown here, not swallowed.
 */
function PublishTemplateDialog({ plan, onOpenChange }: { plan: PlanListItem | null; onOpenChange: (o: boolean) => void }) {
  const navigate = useNavigate()
  const [title, setTitle] = useState('')
  const [summary, setSummary] = useState('')
  const [tags, setTags] = useState('')
  const [isPublic, setIsPublic] = useState(true)
  const [error, setError] = useState<{ code?: string; message: string } | null>(null)
  const [slug, setSlug] = useState<string | null>(null)
  const publish = usePublishTemplate(plan?.id ?? '')
  const submit = async () => {
    if (!plan) return
    setError(null)
    try {
      const r = await publish.mutateAsync({
        title: (title || plan.title).slice(0, 120),
        summary: (summary || plan.idea).slice(0, 400),
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 8),
        isPublic,
      })
      setSlug(r.template.slug)
      toast.success(m.fw_pub_done({ slug: r.template.slug }))
    } catch (e) {
      const err = e instanceof ApiError ? e : null
      setError({ code: err?.code, message: err?.code === 'pro_only' ? m.fw_pub_pro() : err?.code === 'not_enough_evidence' ? m.fw_pub_need_evidence() : (err?.message ?? m.common_error_generic()) })
    }
  }
  return (
    <Dialog open={Boolean(plan)} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{m.fw_pub_title()}</DialogTitle>
          <DialogDescription>{m.fw_pub_desc()}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>{m.fw_pub_field_title()}</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={plan?.title ?? ''} maxLength={120} /></div>
          <div className="space-y-1"><Label>{m.fw_pub_field_summary()}</Label><Textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder={plan?.idea.slice(0, 160) ?? ''} rows={3} maxLength={400} /></div>
          <div className="space-y-1">
            <Label>{m.fw_pub_field_tags()}</Label>
            <Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="midtrans, qris, umkm" />
            <p className="text-[11px] text-muted-foreground">{m.fw_pub_tags_hint()}</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={isPublic} onCheckedChange={setIsPublic} id="tpl-public" aria-label={m.fw_pub_public()} />
            <Label htmlFor="tpl-public" className="font-normal">{m.fw_pub_public()}</Label>
          </div>
          {error ? (
            <div className="space-y-2 rounded-lg border border-status-failed/40 bg-status-failed/5 p-3 text-sm text-status-failed">
              <p>{error.message}</p>
              {error.code === 'pro_only' ? (
                <Button size="sm" variant="secondary" onClick={() => { onOpenChange(false); navigate({ to: '/pricing', search: { voucher: undefined } }) }}>{m.fw_pub_pro_cta()}</Button>
              ) : null}
            </div>
          ) : null}
          {slug ? (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
              <code className="min-w-0 truncate font-mono text-xs">/templates/{slug}</code>
              <Button size="sm" variant="secondary" asChild><Link to="/templates/$slug" params={{ slug }} onClick={() => onOpenChange(false)}>{m.fw_pub_view()}</Link></Button>
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>{m.common_close()}</Button>
          <Button onClick={submit} disabled={publish.isPending || !plan}>{m.fw_pub_submit()}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function PlanDrawer({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { data } = usePlans()
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<Sort>('newest')
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [publishing, setPublishing] = useState<PlanListItem | null>(null)
  const groups = useMemo(() => {
    const plans = (data?.plans ?? []).filter((p) => p.title.toLowerCase().includes(q.toLowerCase()) || p.idea.toLowerCase().includes(q.toLowerCase()))
    plans.sort((a, b) => (sort === 'title' ? a.title.localeCompare(b.title) : sort === 'oldest' ? a.createdAt.localeCompare(b.createdAt) : b.updatedAt.localeCompare(a.updatedAt)))
    const map = new Map<string, { name: string; plans: typeof plans }>()
    for (const p of plans) {
      const g = map.get(p.workspaceId) ?? { name: p.workspaceName, plans: [] }
      g.plans.push(p)
      map.set(p.workspaceId, g)
    }
    return [...map.entries()]
  }, [data, q, sort])
  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b p-4">
          <div className="flex items-center justify-between">
            <SheetTitle>{m.drawer_title()}</SheetTitle>
            <Button asChild size="icon" variant="ghost" className="size-8">
              <Link to="/plan" onClick={() => onOpenChange(false)}><Plus className="size-4" /></Link>
            </Button>
          </div>
          <div className="mt-2 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={m.drawer_search()} className="pl-8" />
            </div>
            <Select value={sort} onValueChange={(v) => setSort(v as Sort)}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">{m.drawer_sort_newest()}</SelectItem>
                <SelectItem value="oldest">{m.drawer_sort_oldest()}</SelectItem>
                <SelectItem value="title">{m.drawer_sort_title()}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-2">
          {!groups.length ? <p className="p-6 text-center text-sm text-muted-foreground">{m.drawer_empty()}</p> : null}
          {groups.map(([wsId, g]) => {
            const c = collapsed[wsId]
            return (
              <div key={wsId} className="mb-1">
                <button className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted" onClick={() => setCollapsed((s) => ({ ...s, [wsId]: !c }))}>
                  <Folder className="size-4 text-muted-foreground" />
                  <span className="flex-1 truncate font-medium">{g.name}</span>
                  <span className="text-xs text-muted-foreground">{m.drawer_plans_count({ n: g.plans.length })}</span>
                  {c ? <ChevronRight className="size-4" /> : <ChevronDown className="size-4" />}
                </button>
                {!c
                  ? g.plans.map((p) => (
                      <div key={p.id} className="ml-6 flex items-center gap-1">
                        <PlanLink plan={p} onClick={() => onOpenChange(false)} className="flex min-w-0 flex-1 flex-col rounded-lg px-2 py-1.5 hover:bg-muted">
                          <span className="flex items-center gap-2">
                            <span className="truncate text-sm">{p.title}</span>
                            {p.status === 'generating' ? <span className="shrink-0 rounded-full bg-status-progress/15 px-1.5 py-px text-[10px] font-medium text-status-progress">{m.ws_plan_generating()}</span> : p.status !== 'completed' ? <span className="shrink-0 rounded-full bg-muted px-1.5 py-px text-[10px] font-medium text-muted-foreground">{m.ws_plan_draft()}</span> : null}
                          </span>
                          <span className="text-[11px] text-muted-foreground">
                            {m.ws_plan_n({ n: p.cycle })} · {fmtDate(p.updatedAt)}
                          </span>
                        </PlanLink>
                        {p.status === 'completed' ? (
                          <Button size="icon" variant="ghost" className="size-7 shrink-0 text-muted-foreground" title={m.fw_pub_action()} aria-label={m.fw_pub_action()} onClick={() => { setPublishing(p); onOpenChange(false) }}>
                            <Share2 className="size-3.5" />
                          </Button>
                        ) : null}
                      </div>
                    ))
                  : null}
              </div>
            )
          })}
        </div>
      </SheetContent>
    </Sheet>
    <PublishTemplateDialog plan={publishing} onOpenChange={(o) => { if (!o) setPublishing(null) }} />
    </>
  )
}
